import React, { useState, useEffect, useCallback, useMemo } from "react";
import { withTranslation, Trans } from "react-i18next";
import { useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import WithSelect from "../../../components/select";
import StatusBadge from "../../../components/statusbadge/StatusBadge";
import * as bulkJobActions from "../../../modules/bulkJobs";
import * as notificationActions from "../../../modules/notification";
import { CONSTANTS } from "@constants";
import { Link } from "react-router-dom";
import CreateAssetsBulkJobMessage from "../../../components/modals/components/CreateAssetsBulkToast";
import menuService from "@services/menuService";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    styled,
    Tooltip,
} from "@mzc-pdc/ui";
import {
    IconArrowDown,
    IconArrowUp,
    IconAssetsAdd,
    IconCancelCircle,
    IconCategory,
    IconCircle,
    IconDownload,
    IconErrorCircle,
    IconFileCopyto,
    IconHierarchyFolded,
    IconOpenInNew,
    IconPath,
    IconProjects,
    IconRetry,
    IconTime,
    IconZip,
    ImageFolderSmallOn,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Typography from "@components_v2/typography/typography";
import { SelectLimitTags } from "@components_v2/select";
import JobEmptyList from "@routes/rightsidebar/ui/job-empty-list";
import State from "@components/state";
import { alpha } from "@mui/material";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { capitalize } from "@utils/convert-string";
import useFolder from "@hooks/useFolder";
import { GridNameEllipsis } from "@components_v2/grid/grid.styled";
import { copyAsset } from "@modules/asset";
import { listJobs } from "../../../modules/bulkJobs";
import { setNotification } from "../../../modules/notification";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";

let throttleHandler = false;

const CreateAssetsPanel = (props) => {
    const { t } = props;
    const location = useLocation();
    const { spaceId, projectId } = menuService.parse(location.pathname);
    const { getVirtualFolderFullPath, getDriveName } = useVirtualFolderPath({ domain: "ASSET" });

    const [limit, setLimit] = useState(20);
    const [viewmoreEnable, setViewmoreEnable] = useState(false);
    const [filterType, setFilterType] = useState([]);
    const [filterStatus, setFilterStatus] = useState([]);

    const { bulkJobs } = useSelector((state) => state);
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const dispatch = useDispatch();

    const BulkJobActions = bindActionCreators(bulkJobActions, dispatch);
    const NotificationActions = bindActionCreators(notificationActions, dispatch);

    useEffect(() => {
        if (bulkJobs.totalCounts > bulkJobs.data.length) {
            setViewmoreEnable(true);
        } else {
            setViewmoreEnable(false);
        }
    }, [bulkJobs.data]);

    useEffect(() => {
        BulkJobActions.updateSearch({ limit });
    }, [limit]);

    const onChangeLimit = useCallback(async (limit) => {
        setLimit(limit);
        await BulkJobActions.listJobs();
    }, []);

    const onSubmit = useCallback(async () => {
        await BulkJobActions.updateSearch({ limit: bulkJobs.search.limit + limit });
        await BulkJobActions.listJobs();
    }, []); //archive, restore(목록페이지에서 job 요청) / -> job 목록 요청 -> job 완료시 task에 일부 실패가 있으면 토스트(panel에서 담당)

    const toggleJobIsExpanded = useCallback(
        async (item) => {
            if (throttleHandler) {
                return;
            }
            if (item.isFolded) {
                const job = {
                    ...item,
                    isFolded: !item.isFolded,
                };
                await BulkJobActions.updateJob({ job });
            } else {
                throttleHandler = true;
                try {
                    const { data } = await BulkJobActions.listTasks({ jobId: item.jobId });
                    const newTasks = data.results || [];
                    await Promise.all(
                        newTasks.map(async (task) => {
                            try {
                                if (task.metadata.virtualFolder?.id) {
                                    task.folderFullPath = await getVirtualFolderFullPath({
                                        folderId: task.metadata.virtualFolder?.id,
                                    });
                                } else if (task.metadata.virtualFolder?.fullpath !== undefined) {
                                    const driveName = await getDriveName(task.metadata.virtualFolder.driveId);
                                    task.folderFullPath = `${driveName}/${task.metadata.virtualFolder.fullpath}`;
                                }
                            } catch (error) {
                                task.folderFullPath = "-";
                                console.log(error);
                            }
                        }),
                    );
                    const job = {
                        ...item,
                        isFolded: !item.isFolded,
                        tasks: newTasks,
                    };
                    console.log(job);
                    await BulkJobActions.updateJob({ job });
                    throttleHandler = false;
                } catch (error) {
                    throttleHandler = false;
                }
            }
        },
        [getVirtualFolderFullPath, getDriveName],
    );

    const getJobRunningStatusByAction = useCallback((action) => {
        switch (action) {
            case "CREATE_ASSETS":
                return "Creating asset...";
            case "ARCHIVE_ASSETS":
                return "Archiving asset...";
            case "RESTORE_ASSETS":
                return "Restoring asset...";
            default:
                return "Creating asset...";
        }
    }, []);

    const renderJobStatus = useCallback((item) => {
        let className, icon, text;

        const action = item.action.toLowerCase().split("_").join(" ");

        switch (item.status) {
            case "SUBMITTED":
                return (
                    <>
                        <CircularProgress size={12} thickness={5} color="primary" />
                        <Typography color="primary.main">{t(`common::label::Pending`, `Pending`)}</Typography>
                    </>
                );
            case "RUNNING":
                const status = getJobRunningStatusByAction(item.action);
                return (
                    <>
                        <CircularProgress size={12} thickness={5} color="primary" />
                        <Typography color="primary.main">{t(`common::label::${status}`, status)}</Typography>
                    </>
                );
            case "SUCCEED":
                return (
                    <Typography color="success.main">
                        {item.partialFailure
                            ? t(`common::label::${"Partial {{type}} to completed."}`, {
                                  type: t(`common::label::${action}`, action),
                              })
                            : t(`common::label::${"{{type}} completed."}`, {
                                  type: t(`common::label::${capitalize(action, true)}`, capitalize(action, true)),
                              })}
                    </Typography>
                );
            case "FAILED":
                return (
                    <Typography color="error.main">
                        {t(`common::label::An error occurred while preparing the asset for {{type}}.`, {
                            type: t(`common::label::${action}`, action),
                        })}
                    </Typography>
                );
            case "CANCELED":
                return <Typography>{t(`common::label::${action} canceled`, `${action} canceled`)}</Typography>;
            default:
                return (
                    <Typography color="primary.main">
                        {item.action === "MOVE" ? `${t(`common::label::Moving`, `Moving`)}...` : ""}
                    </Typography>
                );
        }
    }, []);

    const toastInfoMessage = useCallback((checkedAssets) => {
        NotificationActions.setNotification({
            type: "info",
            autoClose: false,
            contents: (
                <CreateAssetsBulkJobMessage
                    firstAssetMediaType={checkedAssets[0].mediaType}
                    count={checkedAssets.length}
                />
            ),
        });
    }, []);

    const onRetry = useCallback(async (job) => {
        if (job.action === "CREATE_ASSETS") {
            const { data } = await BulkJobActions.listTasks({ jobId: job.jobId });
            const tasks = data.results || [];
            const failedTasks = tasks.filter((t) => t.status === "FAILED");
            if (failedTasks.length > 0) {
                const assets = failedTasks.map((t) => {
                    return t.metadata;
                });
                const newJob = {
                    action: "CREATE_ASSETS",
                    params: {
                        items: assets,
                    },
                };
                await BulkJobActions.createJob(newJob);
                toastInfoMessage(assets);
                await BulkJobActions.listJobs();
            }
        } else if (job.action === "COPY_ASSETS") {
            const metadata = job.metadata;
            const params = {
                destination: {
                    ...metadata.destination,
                },
                source: {
                    assets: [...metadata.source.assets],
                },
            };

            await dispatch(copyAsset(params));
            await dispatch(listJobs());
            await dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::${"Asset {{item}} duplication completed."}`, {
                        item: metadata.source.assets?.[0].name,
                    }),
                }),
            );
        }
    }, []);

    const jobsData = bulkJobs.data;

    const assetJobType = useMemo(
        () => [
            { label: "Asset Bulk Create", value: "CREATE_ASSETS" },
            {
                label: "Duplicate",
                value: "COPY_ASSETS",
            },
        ],
        [],
    );

    const filteredJobsData = useMemo(() => {
        const parsedFilterType = filterType.map(
            (typeLabel) => assetJobType.find((type) => type.label === typeLabel)?.value ?? typeLabel,
        );
        const parsedFilterStatus = filterStatus.map(
            (typeLabel) => CONSTANTS("ASSET_JOB_STATUS").find((type) => type.label === typeLabel)?.value ?? typeLabel,
        );

        return jobsData.filter((job) => {
            if (parsedFilterType.length > 0 && !parsedFilterType.includes(job.action)) {
                return false;
            }
            if (parsedFilterStatus.length > 0 && !parsedFilterStatus.includes(job.status)) {
                return false;
            }
            return true;
        });
    }, [jobsData, filterType, filterStatus, assetJobType]);

    return (
        <React.Fragment>
            <Grid container pt={3} px={3} pb={2} gap={1} justifyContent={"center"} alignItems={"center"}>
                <Grid item xs>
                    <FormControl fullWidth>
                        <InputLabel id="select-label-asset-jobs-type" shrink={true}>
                            {t(`common::label::Type`, `Type`)}
                        </InputLabel>
                        <SelectLimitTags
                            displayEmpty
                            notched
                            label={"Type"}
                            labelId={"select-label-asset-jobs-type"}
                            multiple={true}
                            value={filterType}
                            MenuProps={{
                                disablePortal: true,
                            }}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                            }}
                            limitTags={1}
                        >
                            {assetJobType.map((status) => (
                                <MenuItem
                                    key={`asset-job-type-${status.label}`}
                                    value={status.label}
                                    sx={{ borderRadius: `0 !important`, borderBottom: "none !important" }}
                                >
                                    <Checkbox color={"greySecondary"} checked={filterType.includes(status.label)} />
                                    {t(`common::label::${status.label}`, status.label)}
                                </MenuItem>
                            ))}
                        </SelectLimitTags>
                    </FormControl>
                </Grid>
                <Grid item xs>
                    <FormControl fullWidth>
                        <InputLabel id="select-label-asset-jobs-status" shrink={true}>
                            {t(`common::label::Status`, `Status`)}
                        </InputLabel>
                        <SelectLimitTags
                            displayEmpty
                            notched
                            label={"Status"}
                            multiple={true}
                            value={filterStatus}
                            MenuProps={{
                                disablePortal: true,
                            }}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                            }}
                            limitTags={1}
                        >
                            {CONSTANTS("ASSET_JOB_STATUS").map((status) => (
                                <MenuItem
                                    key={`asset-job-status-${status.value}`}
                                    value={status.label}
                                    sx={{
                                        borderRadius: `0 !important`,
                                        borderBottom: "none !important ",
                                    }}
                                >
                                    <Checkbox color={"greySecondary"} checked={filterStatus.includes(status.label)} />
                                    {t(`common::label::${status.label}`, status.label)}
                                </MenuItem>
                            ))}
                        </SelectLimitTags>
                    </FormControl>
                </Grid>
            </Grid>
            <Divider sx={{ mx: 3 }} />
            <Stack flex={1} px={3} py={2.5} gap={4}>
                {filteredJobsData.length > 0 ? (
                    <>
                        {filteredJobsData.map((item) => {
                            return (
                                <Box>
                                    <Grid container pb={1} gap={1} alignItems={"center"}>
                                        <Grid item>
                                            <Grid
                                                container
                                                alignItems={"center"}
                                                justifyContent={"center"}
                                                width={24}
                                                height={20}
                                                bgcolor={"greySecondary.main"}
                                                color={"greySecondary.contrastText"}
                                                borderRadius={0.7}
                                            >
                                                {item.action === "COPY_ASSETS" && <IconFileCopyto size={16} />}
                                                {item.action === "CREATE_ASSETS" && <IconAssetsAdd size={16} />}
                                            </Grid>
                                        </Grid>
                                        <Divider flexItem orientation={"vertical"} sx={{ my: 1 }} />
                                        <Grid item xs>
                                            <Grid container gap={1} alignItems={"center"}>
                                                <State
                                                    variant={"contained"}
                                                    status={
                                                        item.action === "ARCHIVE_ASSETS" &&
                                                        item.status === "SUCCEED" &&
                                                        !item.summary?.detail?.successCount
                                                            ? "FAILED"
                                                            : item.status
                                                    }
                                                />
                                                {(item.status === "FAILED" || item.partialFailure) && (
                                                    <Tooltip
                                                        title={
                                                            item.summary?.exitCode !== "SUCCESS"
                                                                ? t(
                                                                      `common::code::${item.summary.exitCode}`,
                                                                      item.summary.exitCode,
                                                                  )
                                                                : t(
                                                                      `common::label::This operation was not entirely successful.`,
                                                                      "This operation was not entirely successful.",
                                                                  )
                                                        }
                                                    >
                                                        <IconButton circled size="small">
                                                            <IconErrorCircle size={16} color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            {(item.status === "FAILED" || item.partialFailure) && (
                                                <Button
                                                    variant={"text"}
                                                    size={"small"}
                                                    color={"greySecondary"}
                                                    startIcon={<IconRetry size={16} />}
                                                    onClick={() => onRetry(item)}
                                                >
                                                    {t(`common::label::Retry`, `Retry`)}
                                                </Button>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                    <Stack py={2.5} px={1.5} gap={1.5}>
                                        <Grid container gap={1}>
                                            <Grid item xs={3}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                    {t(`common::label::Job`, `Job`)}
                                                </Grid>
                                            </Grid>
                                            <Grid item>
                                                {t(
                                                    `common::label::${
                                                        item.action === "CREATE_ASSETS"
                                                            ? "Asset Bulk Create"
                                                            : "Asset Duplicate"
                                                    }`,
                                                    item.action === "CREATE_ASSETS"
                                                        ? "Asset Bulk Create"
                                                        : "Asset Duplicate",
                                                )}
                                            </Grid>
                                        </Grid>
                                        <Grid container gap={1}>
                                            <Grid item xs={3}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                    {t(`common::label::Job ID`, `Job ID`)}
                                                </Grid>
                                            </Grid>
                                            <Grid item>{item.jobId ?? "-"}</Grid>
                                        </Grid>
                                        {item.startAt && (
                                            <Grid container gap={1}>
                                                <Grid item xs={3}>
                                                    <Grid container gap={1} alignItems={"center"}>
                                                        <IconCircle
                                                            size={6}
                                                            htmlColor={theme.palette.greySecondary[500]}
                                                        />
                                                        {t(`common::label::Start date`, `Start date`)}
                                                    </Grid>
                                                </Grid>
                                                <Grid item>{`${moment(item.startAt).format(
                                                    "YYYY-MM-DD HH:mm:ss",
                                                )}`}</Grid>
                                            </Grid>
                                        )}
                                        {item.endAt && (
                                            <Grid container gap={1}>
                                                <Grid item xs={3}>
                                                    <Grid container gap={1} alignItems={"center"}>
                                                        <IconCircle
                                                            size={6}
                                                            htmlColor={theme.palette.greySecondary[500]}
                                                        />
                                                        {t(`common::label::End date`, `End date`)}
                                                    </Grid>
                                                </Grid>
                                                <Grid item>{`${moment(item.endAt).format(
                                                    "YYYY-MM-DD HH:mm:ss",
                                                )}`}</Grid>
                                            </Grid>
                                        )}
                                    </Stack>

                                    <Collapse in={!!item.isFolded} collapsedSize={75}>
                                        <Box
                                            px={1}
                                            py={1.5}
                                            borderRadius={1}
                                            bgcolor={alpha(theme.palette.greySecondary[50], 0.5)}
                                            maxHeight={500}
                                            overflow={"auto"}
                                        >
                                            <Grid container gap={1.5}>
                                                <Grid item>
                                                    <Tooltip
                                                        title={t(
                                                            `common::label::${!item.isFolded ? "Expand" : "Fold"}`,
                                                            !item.isFolded ? "Expand" : "Fold",
                                                        )}
                                                    >
                                                        <IconButton
                                                            circled
                                                            size="xxsmall"
                                                            onClick={() => toggleJobIsExpanded(item)}
                                                            // disabled={item.tasks.length === 0}
                                                            sx={{ width: 20, height: 20 }}
                                                        >
                                                            {!item.isFolded ? (
                                                                <IconArrowDown size={12} />
                                                            ) : (
                                                                <IconArrowUp size={12} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item xs>
                                                    <Stack gap={1.5}>
                                                        <Grid container>
                                                            <Grid item xs>
                                                                <Grid
                                                                    container
                                                                    mt={0.25}
                                                                    mb={1.5}
                                                                    alignItems={"center"}
                                                                >
                                                                    <Trans
                                                                        i18nKey="common::msg::Detailed view of <0> {{number}} </0> tasks"
                                                                        values={{ number: item.taskCount || 0 }}
                                                                        components={[
                                                                            <Typography
                                                                                variant={"body3_700"}
                                                                                color="primary.main"
                                                                            ></Typography>,
                                                                        ]}
                                                                    />
                                                                </Grid>
                                                                <Grid container gap={0.75} alignItems={"center"}>
                                                                    {renderJobStatus(item)}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                    {/* 대량 생성 */}

                                                    {item.isFolded &&
                                                        item.action === "CREATE_ASSETS" &&
                                                        item.tasks?.map((task) => (
                                                            <Stack py={1.5} gap={2}>
                                                                <Box>
                                                                    <Grid container alignItems={"center"}>
                                                                        <AssetTypeFlag
                                                                            width={20}
                                                                            height={16}
                                                                            type={task.metadata.mediaType}
                                                                        />
                                                                        <GridNameEllipsis item>
                                                                            <Typography variant="body2_500">
                                                                                {task.metadata.name}
                                                                            </Typography>
                                                                        </GridNameEllipsis>
                                                                    </Grid>
                                                                    {task.folderFullPath && (
                                                                        <Grid
                                                                            container
                                                                            gap={1}
                                                                            mt={0.75}
                                                                            px={3.5}
                                                                            flexWrap={"nowrap"}
                                                                        >
                                                                            <IconPath
                                                                                size={16}
                                                                                htmlColor={
                                                                                    theme.palette.greySecondary[500]
                                                                                }
                                                                            />
                                                                            <Typography
                                                                                variant="body3_400"
                                                                                color={"text.secondary"}
                                                                            >
                                                                                {task.folderFullPath ?? "-"}
                                                                            </Typography>
                                                                        </Grid>
                                                                    )}
                                                                </Box>
                                                            </Stack>
                                                        ))}
                                                    {/* //대량 생성 */}
                                                    {/*/!* 복제 *!/*/}
                                                    {item.isFolded &&
                                                        item.tasks?.length > 0 &&
                                                        item.action === "COPY_ASSETS" && (
                                                            <Stack py={1.5} gap={2}>
                                                                <Box>
                                                                    <Typography
                                                                        variant="body3_700"
                                                                        color="text.secondary"
                                                                    >
                                                                        {t(`common::label::Source`, `Source`)}
                                                                    </Typography>
                                                                    <Stack mt={1} mb={2} px={1} gap={1}>
                                                                        <Grid
                                                                            container
                                                                            gap={1}
                                                                            alignItems={"center"}
                                                                            flexWrap={"nowrap"}
                                                                        >
                                                                            <IconProjects
                                                                                size={16}
                                                                                htmlColor={
                                                                                    theme.palette.greySecondary[500]
                                                                                }
                                                                            />
                                                                            <Typography variant="body3_400">
                                                                                {stage.projects.find(
                                                                                    ({ id }) =>
                                                                                        id === item.metadata.projectId,
                                                                                )?.name ?? "-"}
                                                                            </Typography>
                                                                        </Grid>
                                                                    </Stack>
                                                                    <Grid container alignItems={"center"}>
                                                                        <AssetTypeFlag
                                                                            width={20}
                                                                            height={16}
                                                                            type={item.tasks[0]?.metadata?.mediaType}
                                                                        />
                                                                        <GridNameEllipsis item maxWidth={"250px"}>
                                                                            <Typography variant="body2_500">
                                                                                {item.tasks[0]?.metadata?.name}
                                                                            </Typography>
                                                                        </GridNameEllipsis>
                                                                        <Grid item>
                                                                            {item.tasks?.length > 1 && (
                                                                                <>
                                                                                    <Typography variant="body3_400">
                                                                                        <Trans
                                                                                            i18nKey="common::label::and <emp>{{count}}</emp> others"
                                                                                            components={{
                                                                                                emp: (
                                                                                                    <Typography
                                                                                                        variant="body3_700"
                                                                                                        color="primary.main"
                                                                                                    ></Typography>
                                                                                                ),
                                                                                            }}
                                                                                            values={{
                                                                                                count:
                                                                                                    item.tasks.length -
                                                                                                    1,
                                                                                            }}
                                                                                        />
                                                                                    </Typography>
                                                                                </>
                                                                            )}
                                                                        </Grid>
                                                                    </Grid>
                                                                    <Divider sx={{ my: 2 }} />
                                                                    <Typography
                                                                        variant="body3_700"
                                                                        color="text.secondary"
                                                                    >
                                                                        {t(`common::label::Destination`, `Destination`)}
                                                                    </Typography>
                                                                    <Stack mt={1} px={1} gap={1}>
                                                                        <Grid
                                                                            container
                                                                            gap={1}
                                                                            alignItems={"center"}
                                                                            flexWrap={"nowrap"}
                                                                        >
                                                                            <IconProjects
                                                                                size={16}
                                                                                htmlColor={
                                                                                    theme.palette.greySecondary[500]
                                                                                }
                                                                            />
                                                                            <Typography variant="body3_400">
                                                                                {stage.projects.find(
                                                                                    ({ id }) =>
                                                                                        id ===
                                                                                        item.metadata.destination
                                                                                            .project?.id,
                                                                                )?.name ?? "-"}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            container
                                                                            gap={1}
                                                                            alignItems={"center"}
                                                                            flexWrap={"nowrap"}
                                                                        >
                                                                            <IconPath
                                                                                size={16}
                                                                                htmlColor={
                                                                                    theme.palette.greySecondary[500]
                                                                                }
                                                                            />
                                                                            <Typography variant="body3_400">
                                                                                {item.metadata.destination
                                                                                    ?.virtualFolder?.name ?? "-"}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid container gap={1} flexWrap={"nowrap"}>
                                                                            <Grid item>
                                                                                <IconCategory
                                                                                    size={16}
                                                                                    htmlColor={
                                                                                        theme.palette.greySecondary[500]
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                            <Grid item>
                                                                                <Grid container gap={0.5}>
                                                                                    {item.metadata.destination
                                                                                        ?.categories?.length === 0 ? (
                                                                                        "-"
                                                                                    ) : (
                                                                                        <>
                                                                                            {item.metadata.destination?.categories?.map(
                                                                                                (category) => (
                                                                                                    <Chip
                                                                                                        label={
                                                                                                            category.name
                                                                                                        }
                                                                                                        size="small"
                                                                                                        variant="outlined"
                                                                                                    />
                                                                                                ),
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Stack>
                                                                </Box>
                                                                <Box>
                                                                    {item.tasks?.map((task) => (
                                                                        <>
                                                                            <Grid
                                                                                container
                                                                                alignItems={"center"}
                                                                                gap={1}
                                                                            >
                                                                                <Grid item sx={{ flexGrow: 0 }}>
                                                                                    <IconHierarchyFolded
                                                                                        size={6}
                                                                                        htmlColor={
                                                                                            theme.palette
                                                                                                .greySecondary[500]
                                                                                        }
                                                                                        sx={{ mr: theme.spacing(1) }}
                                                                                    />
                                                                                    <AssetTypeFlag
                                                                                        width={20}
                                                                                        height={16}
                                                                                        type={task.metadata?.mediaType}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid item sx={{ display: "flex" }}>
                                                                                    <GridNameEllipsis
                                                                                        item
                                                                                        maxWidth={"110px"}
                                                                                    >
                                                                                        <Typography variant="body2_500">
                                                                                            {task.metadata
                                                                                                ?.desiredName ??
                                                                                                task.metadata?.name}
                                                                                        </Typography>
                                                                                    </GridNameEllipsis>
                                                                                    {task.status === "SUCCEED" &&
                                                                                        task.metadata?.desiredId && (
                                                                                            <Grid item>
                                                                                                <Typography
                                                                                                    variant="body3_400"
                                                                                                    color={
                                                                                                        "primary.main"
                                                                                                    }
                                                                                                    sx={{ ml: 1 }}
                                                                                                >
                                                                                                    (
                                                                                                    <Link
                                                                                                        href="#"
                                                                                                        to={menuService.paths.assetsAssetDetail(
                                                                                                            stage.id,
                                                                                                            project.id,
                                                                                                            task
                                                                                                                .metadata
                                                                                                                .desiredId,
                                                                                                        )}
                                                                                                        underline="hover"
                                                                                                    >
                                                                                                        {
                                                                                                            task
                                                                                                                .metadata
                                                                                                                ?.desiredId
                                                                                                        }
                                                                                                        <IconOpenInNew
                                                                                                            size={12}
                                                                                                        />
                                                                                                    </Link>
                                                                                                    )
                                                                                                </Typography>
                                                                                            </Grid>
                                                                                        )}
                                                                                </Grid>
                                                                                <Grid item>
                                                                                    {task.status === "FAILED" && (
                                                                                        <Tooltip
                                                                                            title={t(
                                                                                                `common::code::${task.summary.exitMessage}`,
                                                                                                task.summary
                                                                                                    .exitMessage ?? "",
                                                                                            )}
                                                                                        >
                                                                                            <IconButton
                                                                                                circled
                                                                                                size="small"
                                                                                            >
                                                                                                <IconErrorCircle
                                                                                                    size={16}
                                                                                                    color="error"
                                                                                                />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </Grid>
                                                                            </Grid>
                                                                            <Grid
                                                                                container
                                                                                mt={0.75}
                                                                                px={2}
                                                                                alignItems="center"
                                                                            >
                                                                                <Typography
                                                                                    variant="body3_400"
                                                                                    color={"text.secondary"}
                                                                                    sx={{ mr: theme.spacing(1) }}
                                                                                >
                                                                                    원본 :
                                                                                </Typography>
                                                                                <GridNameEllipsis
                                                                                    item
                                                                                    maxWidth={"90px"}
                                                                                >
                                                                                    <Typography variant="body3_400">
                                                                                        {task.metadata?.name}
                                                                                    </Typography>
                                                                                </GridNameEllipsis>
                                                                                <Grid item sx={{ flex: 2 }}>
                                                                                    <Typography
                                                                                        variant="body3_400"
                                                                                        color={"primary.main"}
                                                                                        sx={{ ml: 1 }}
                                                                                    >
                                                                                        (
                                                                                        <Link
                                                                                            href="#"
                                                                                            to={menuService.paths.assetsAssetDetail(
                                                                                                stage.id,
                                                                                                project.id,
                                                                                                task.metadata.id,
                                                                                            )}
                                                                                            underline="hover"
                                                                                        >
                                                                                            {task.metadata?.id}
                                                                                            <IconOpenInNew size={12} />
                                                                                        </Link>
                                                                                        )
                                                                                    </Typography>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </>
                                                                    ))}
                                                                </Box>
                                                            </Stack>
                                                        )}
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </>
                ) : (
                    <JobEmptyList />
                )}
            </Stack>
            {jobsData.length > 0 && (
                <div className="panel-footer" style={{ flex: 0, paddingRight: "10px" }}>
                    {viewmoreEnable ? (
                        <button
                            type={"button"}
                            className={"btn btn-outline-default"}
                            onClick={(e) => {
                                e.preventDefault();
                                onSubmit();
                            }}
                        >
                            <span>{`View More`}</span>
                            <i className="sprite sprite-angle-down" />
                        </button>
                    ) : (
                        ""
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default withTranslation()(CreateAssetsPanel);
